package ui.license.detail

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.CurrencyType
import api.LicenseState
import common.frontend.api.Io
import kotlinx.datetime.LocalDate
import org.ssoft.common.tools.EMPTY
import org.ssoft.common.tools.nowDate
import provider.LocalAccountProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.args
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.widget.ListingSpecs
import kotlin.time.Clock


interface LicenseDetailScope {

    val vm: LicenseDetailVM
    val action: (LicenseDetailVM.Action) -> Unit
    val nav: Navigation
    val login: Io.User
    val args: LicenseArgs

    companion object {

        val headers = ListingSpecs.create<Wrapper.Item>(
            ListingSpecs.Item(title = Res.string.license_listing__item_id, width = 50.dp, align = TextAlign.Center, lines = 1) { ListingSpecs.trimmedId(it.data.id) },
            ListingSpecs.Item(title = Res.string.license_listing__item_state, width = 128.dp, align = TextAlign.Start) {
                LicenseState[it.data.state].stringRes().rememberString()
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_price_device, width = 90.dp, align = TextAlign.End) {
                "${it.data.price} ${it.currency.symbol}"
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_dt_publish, width = 90.dp, align = TextAlign.Center) {
                if (it.data.upload == LocalDate.EMPTY) "-"
                else it.data.upload.formatFromRes()
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_dt_start, width = 90.dp, align = TextAlign.Center) {
                if (it.data.start == LocalDate.EMPTY) "-"
                else it.data.start.formatFromRes()
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_dt_end, width = 90.dp, align = TextAlign.Center) {
                if (it.data.end == LocalDate.EMPTY) "-"
                else it.data.end.formatFromRes()
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_dt_approved, width = 90.dp, align = TextAlign.Center) {
                if (it.data.approved == LocalDate.EMPTY) "-"
                else it.data.approved.formatFromRes()
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_dt_rejected, width = 90.dp, align = TextAlign.Center) {
                if (it.data.reject == LocalDate.EMPTY) "-"
                else it.data.reject.formatFromRes()
            },
        )

        @Composable
        fun rememberScope(): LicenseDetailScope {
            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val vm = nav.viewModel(LicenseDetailVM::class)
            val args = LocalNavigationArgs.current.args<LicenseArgs>()
            val action = LicenseDetailVM.Action.rememberAction(vm)


            return remember {

                object : LicenseDetailScope {
                    override val vm: LicenseDetailVM
                        get() = vm
                    override val action: (LicenseDetailVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: LicenseArgs
                        get() = args
                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val licenses: Io.CompanyLicenseHistory,
        var canAdd: Boolean,
        var canEdit: Boolean,
        var canTerminate: Boolean,
    ) : EFieldList<Io.License, Wrapper.Item>(
        items = licenses.items,
        onBindEntry = {
            Item(
                data = it,
                currency = CurrencyType[licenses.currency],
            )
        }
    ) {

        class Item(
            override val data: Io.License,
            override val key: String = data.id.toString(),
            val currency: CurrencyType,
        ) : Entry<Io.License>

        class CreatorItem(
            var price: Int = 20,
            var start: LocalDate = Clock.nowDate()
        ) {

            val priceField = EFieldTextInt(
                property = ::price,
                condition = { it.trim().toIntOrNull()?.takeIf { it > 0 } })

            val startField = EFieldRegularGeneric(
                property = ::start
            )
        }
    }
}
